import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { geohashForLocation } from 'geofire-common';
import firebase from 'firebase/app';
import { Formik } from 'formik';
import merge from 'lodash/merge';
import {
  Box,
  Input,
  Text,
  HStack,
  Button,
  Textarea,
  ButtonGroup,
  Alert,
  AlertDescription,
  AspectRatio,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  useDisclosure,
  FormLabel,
  FormControl,
  Spacer,
  VStack,
  InputGroup,
  InputLeftAddon,
  Badge,
  SimpleGrid,
  Flex,
  useToast,
  AlertTitle,
  Divider,
  IconButton,
  Link,
  Switch,
  Grid,
} from '@chakra-ui/react';
import { POSITION_CLASSES, formatGoogleLocationResult } from '../../utils';
import { formDefaults, validationSchema } from './constants';
import { db } from '../../api/firebase';
import { maxImageUploadSize, map as mapConfig } from '../../app/config';
import { Minimap, FullscreenImage } from '../../components';
import { reverseGeo } from '../../api/geocoding';

const SubmitFormModal = ({
  defaultOpen = false,
  defaultHoopLocation,
  defaultIsHalfcourt = false,
  position = 'bottomcenter',
  onAfterOpen,
  onAfterClose,
  handleSubmitSuccess,
}) => {
  const [ isLoading, setIsLoading ] = useState(false);
  const [ currentStep, setCurrentStep ] = useState(1);
  const [ imgFullScreenOpen, setImgFullScreenOpen ] = useState(false);
  const { isOpen: modalIsOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const toast = useToast();
  const fileInputRef = useRef();

  const handleModalOpen = () => {
    onModalOpen();
    onAfterOpen();
  };

  useEffect(() => {
    if (defaultOpen) {
      handleModalOpen();
    }
  });

  const handleModalClose = ({ formikBag }) => {
    if (formikBag) {
      formikBag.resetForm({
        touched: {},
        dirty: {},
        errors: {},
      });
    }

    setCurrentStep(1);
    onModalClose();
    onAfterClose();
  };

  const handleSubmit = async (formikBag) => {
    try {
      setIsLoading(true);

      const {
        imageFile,
        caption,
        contributorEmail,
        contributorInstagram,
        isHalfcourtHoop,
        location,
      } = formikBag.values;

      // Upload image to firebase storage
      const cleanFileName = `${contributorEmail}_${Date.now()}`;
      const storageRef = firebase.storage().ref();
      const currentImageRef = storageRef.child(`images/${cleanFileName}`);
      const imageResponse = await currentImageRef.put(imageFile);
      const imageMeta = await imageResponse.ref.getMetadata();
      const imageUrl = await imageResponse.ref.getDownloadURL();

      // Create new submission
      const docToCreate = {
        caption,
        isHalfcourtHoop,
        contributor: {
          email: contributorEmail,
          instagramHandle: contributorInstagram,
        },
        location: {
          geohash: geohashForLocation([ location.latitude, location.longitude ]),
          coords: new firebase.firestore.GeoPoint(location.latitude, location.longitude),
          ...location,
        },
        image: {
          url: imageUrl,
          name: imageMeta.name,
          size: imageMeta.size,
          contentType: imageMeta.contentType,
        },
        createdDateTime: firebase.firestore.Timestamp.fromDate(new Date()),
      };

      // Add the document id
      const newDoc = await db.collection('submissions').add(docToCreate);
      newDoc.update({ id: newDoc.id });

      setIsLoading(false);
      toast({
        title: 'Thank you for your submission',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      handleModalClose({ formikBag });
      handleSubmitSuccess();
    } catch (error) {
      toast({
        title: 'An error occurred, unable to submit',
        description: `${error.name}: ${error.code}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  const onClickNext = () => {
    if (currentStep === 1) {
      setCurrentStep(currentStep + 1);
    }

    if (currentStep === 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleRemoveImage = (formikBag) => {
    formikBag.setFieldValue('imgSrc', formikBag.initialValues.imgSrc);
    formikBag.setFieldValue('imageFile', '');
    URL.revokeObjectURL(formikBag.values.imgSrc);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageFileChange = (event, formikBag) => {
    if (event.target) {
      const file = event.target.files[0];

      if (!file) return;

      if (file.size > maxImageUploadSize) {
        alert('Please select a smaller file (under 15 MB)');
        return;
      }

      const newSrc = URL.createObjectURL(file);
      formikBag.setFieldValue('imgSrc', newSrc);
      formikBag.setFieldValue('imageFile', file);
    }
  };

  const initialValues = defaultHoopLocation
    ? merge({}, formDefaults, defaultHoopLocation)
    : merge({}, formDefaults, { isHalfcourtHoop: defaultIsHalfcourt });

  return (
    <div className={POSITION_CLASSES[position]}>
      <div className="leaflet-control">
        <Formik
          initialValues={initialValues}
          validateOnMount
          enableReinitialize
          validateOnChange={false}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formikBag) => (
            <form onSubmit={formikBag.handleSubmit}>
              <Modal
                isOpen={modalIsOpen}
                onClose={() => handleModalClose({ formikBag })}
                size="6xl"
                isCentered
                motionPreset="none"
                scrollBehavior="inside"
                closeOnOverlayClick={false}
                blockScrollOnMount={false}
                initialFocusRef={fileInputRef}
                id="submission-form-modal"
              >
                <ModalOverlay bg="blackAlpha.600" />
                <ModalContent
                  boxShadow="none"
                  borderRadius="0"
                  mx={[ '0', '8' ]}
                  my={[ '0', '8' ]}
                  h="full"
                  maxW={[ 'unset', '750px' ]}
                  maxH={[ 'unset', '90vh' ]}
                >
                  <ModalHeader>Submit Hoops</ModalHeader>
                  <ModalBody p="0">
                    <IconButton
                      onClick={() => handleModalClose({ formikBag })}
                      aria-label="Close"
                      icon={<FontAwesomeIcon icon={[ 'fas', 'times' ]} size="lg" />}
                      size="sm"
                      colorScheme="blackAlpha"
                      pos="absolute"
                      right={[ '2', '-4' ]}
                      top={[ '2', '-4' ]}
                      rounded="full"
                    />
                    {currentStep === 1 && (
                      <VStack px="6">
                        <FormControl id="imageFile" isRequired textAlign="end">
                          <FormLabel>Upload image</FormLabel>
                          <Box
                            className="file-drop-container"
                            bg={formikBag.values.imageFile ? 'gray.100' : 'transparent'}
                            borderRadius="md"
                            maxH="40vh"
                            borderWidth={formikBag.values.imageFile ? '0px' : '2px'}
                            borderStyle="dashed"
                            borderColor={(formikBag.touched.imageFile) ? '#DB939D' : 'var(--color-brand-blue)'}
                            errorBorderColor="#DB939D"
                            sx={{ ':active, :hover': { borderColor: 'var(--color-brand-blue)' } }}
                          >
                            <Input
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              isInvalid={formikBag.touched.imageFile || formikBag.errors.imageFile}
                              onChange={(event) => handleImageFileChange(event, formikBag)}
                              onBlur={formikBag.handleBlur}
                            />
                            <AspectRatio w="full" ratio={1 / 1}>
                              <Image
                                src={formikBag.values.imgSrc}
                                w={formikBag.values.imageFile ? 'full' : '0'}
                                objectFit="cover"
                                margin="auto"
                                maxW="40vh"
                                h="inherit"
                              />
                            </AspectRatio>
                            {!formikBag.values.imageFile && (
                              <VStack
                                zIndex="docked"
                                spacing="4"
                                position="absolute"
                              >
                                <FontAwesomeIcon
                                  size="3x"
                                  className="file-upload-icon"
                                  icon={[ 'fas', 'upload' ]}
                                  color="var(--color-brand-blue)"
                                />
                                <Text color="gray.800" fontSize="sm">
                                  Click to select, or drag and drop a hoop
                                </Text>
                              </VStack>
                            )}
                          </Box>
                          {formikBag.values.imageFile && (
                            <Button
                              variant="ghost"
                              size="xs"
                              mt="2"
                              colorScheme="red"
                              rightIcon={<FontAwesomeIcon size="lg" icon={[ 'fas', 'times' ]} />}
                              onClick={() => handleRemoveImage(formikBag)}
                            >
                              Remove image
                            </Button>
                          )}
                        </FormControl>
                        <Spacer />
                        <Grid
                          width="full"
                          templateColumns="repeat(2, 1fr)"
                          templateRows="repeat(2, 1fr)"
                          columnGap="4"
                        >
                          <FormControl id="contributorInstagram" isRequired>
                            <FormLabel>Instagram</FormLabel>
                            <InputGroup>
                              <InputLeftAddon pointerEvents="none" children="@" />
                              <Input
                                placeholder="Instagram"
                                autoComplete="off"
                                autoCapitalize="none"
                                autoCorrect="off"
                                value={formikBag.values.contributorInstagram}
                                onChange={formikBag.handleChange}
                                onBlur={formikBag.handleBlur}
                                errorBorderColor="#DB939D"
                                isInvalid={formikBag.touched.contributorInstagram && formikBag.errors.contributorInstagram}
                              />
                            </InputGroup>
                          </FormControl>
                          <FormControl id="contributorEmail" isRequired>
                            <FormLabel>Email</FormLabel>
                            <Input
                              type="email"
                              autoComplete="email"
                              placeholder="Your Email"
                              value={formikBag.values.contributorEmail}
                              onChange={formikBag.handleChange}
                              onBlur={formikBag.handleBlur}
                              errorBorderColor="#DB939D"
                              isInvalid={formikBag.touched.contributorEmail && formikBag.errors.contributorEmail}
                            />
                          </FormControl>
                          <FormControl id="caption">
                            <FormLabel>Caption</FormLabel>
                            <Textarea
                              placeholder="Caption this photo"
                              value={formikBag.values.caption}
                              onChange={formikBag.handleChange}
                              onBlur={formikBag.handleBlur}
                              rows={2}
                            />
                          </FormControl>
                        </Grid>
                      </VStack>
                    )}

                    {currentStep === 2 && (
                      <VStack align="stretch" px="6">
                        <HStack>
                          <FormControl id="location" isReadOnly>
                            <FormLabel fontSize="sm">
                              {formikBag.values.location.found
                                ? 'Zoom in & Drag the pin to the most accurate position'
                                : 'Search for your hoop location'}
                            </FormLabel>
                            <Input hidden />
                          </FormControl>
                        </HStack>
                        <VStack spacing="0">
                          {formikBag.values.location.found && (
                            <Alert
                              status="success"
                              bg="brand.blue.500"
                              color="blackAlpha.700"
                              variant="solid"
                              flexDir="column"
                              alignItems="flex-start"
                            >
                              <Flex w="full" align="center">
                                <FontAwesomeIcon icon={[ 'far', 'check-circle' ]} size="lg" />
                                <AlertTitle marginLeft="2">Found location</AlertTitle>
                              </Flex>
                              <AlertDescription fontSize="sm">
                                {`${formikBag.values.location.formatted_address}`}
                              </AlertDescription>
                            </Alert>
                          )}
                          <Minimap
                            zoom={formikBag.values.location.found ? 15 : 3}
                            height={400}
                            isMarkerDraggable
                            isSearchable
                            withGeolocator
                            initialCenter={formikBag.values.location.found
                              ? { lat: formikBag.values.location.latitude, lng: formikBag.values.location.longitude }
                              : { lat: mapConfig.defaultCenter[0], lng: mapConfig.defaultCenter[1] }}
                            onNewSearch={(searchResult) => {
                              if (searchResult) {
                                const formattedResult = formatGoogleLocationResult(searchResult);
                                const { zip, route } = formattedResult;

                                if ((!zip && !route)) {
                                  formikBag.setFieldError('location', 'No specific address found. Please confirm');
                                } else {
                                  formikBag.setErrors({});
                                }
                                const updatedLocation = {
                                  ...formikBag.values.location,
                                  ...formattedResult,
                                  found: true,
                                  isNotConsideredExactAddress: false,
                                };
                                formikBag.setFieldValue('location', updatedLocation, false);
                              }
                            }}
                            handleGeolocate={async (latlng) => {
                              const locations = await reverseGeo(latlng);

                              if (locations.results.length > 0) {
                                const formattedResult = formatGoogleLocationResult(locations.results[0]);
                                const updatedLocation = {
                                  ...formikBag.values.location,
                                  ...formattedResult,
                                  found: true,
                                };
                                formikBag.setFieldValue('location', updatedLocation, false);
                                formikBag.setErrors({});
                              }
                            }}
                            onChangeLatLng={async (newCenter) => {
                              const locations = await reverseGeo({ lat: newCenter.lat, lng: newCenter.lng });

                              if (locations.results.length > 0) {
                                const formattedResult = formatGoogleLocationResult(locations.results[0]);
                                const { zip, route } = formattedResult;

                                if ((!zip && !route)) {
                                  formikBag.setFieldError('location', 'No specific address found. Please confirm');
                                } else {
                                  formikBag.setErrors({});
                                }
                                const updatedLocation = {
                                  ...formikBag.values.location,
                                  ...formattedResult,
                                  latitude: newCenter.lat,
                                  longitude: newCenter.lng,
                                  found: true,
                                  isNotConsideredExactAddress: false,
                                };
                                formikBag.setFieldValue('location', updatedLocation, false);
                              }
                            }}
                          />
                        </VStack>
                        {(!formikBag.values.location.zip && !formikBag.values.location.route) && (
                          <VStack align="center" mb="4">
                            <Text color="brand.red.600" fontWeight="semibold">
                              No specific address found. Please confirm
                            </Text>
                            <FormControl
                              id="isNotConsideredExactAddress"
                              display="flex"
                              alignItems="center"
                              w="auto"
                            >
                              <FormLabel mb="0">
                                I don&apos;t have an exact address
                              </FormLabel>
                              <Switch
                                isChecked={
                                  formikBag.values.location.isNotConsideredExactAddress === true
                                }
                                isInvalid={
                                  formikBag.errors.location
                                    || formikBag.values.location.isNotConsideredExactAddress === false
                                }
                                colorScheme="brand.red"
                                size="lg"
                                value={formikBag.values.location.isNotConsideredExactAddress}
                                onChange={(event) => {
                                  formikBag.setFieldValue(
                                    'location',
                                    { ...formikBag.values.location, isNotConsideredExactAddress: event.target.checked },
                                    true,
                                  );
                                  if (event.target.checked === true) {
                                    formikBag.setFieldError('location', '');
                                  } else {
                                    formikBag.setFieldError('location', 'No specific address found. Please confirm');
                                  }
                                }}
                              />
                            </FormControl>
                          </VStack>
                        )}
                      </VStack>
                    )}

                    {currentStep === 3 && (
                      <Box>
                        {formikBag.values.imageFile && (
                          <Flex bg="gray.100" maxH="40vh" overflow="hidden" position="relative">
                            <IconButton
                              onClick={() => setImgFullScreenOpen(true)}
                              aria-label="Close"
                              icon={<FontAwesomeIcon icon={[ 'fas', 'expand-alt' ]} size="lg" />}
                              colorScheme="blackAlpha"
                              size="sm"
                              pos="absolute"
                              right="2"
                              bottom="2"
                              rounded="full"
                              zIndex="overlay"
                            />
                            <AspectRatio w="full" ratio={1 / 1}>
                              <Image
                                src={URL.createObjectURL(formikBag.values.imageFile)}
                                objectFit="cover"
                                margin="auto"
                                h="inherit"
                                maxW="40vh"
                              />
                            </AspectRatio>
                          </Flex>
                        )}
                        <Alert colorScheme="brand.blue.500" color="blackAlpha.700">
                          <FontAwesomeIcon icon={[ 'far', 'check-circle' ]} size="lg" fixedWidth />
                          <AlertTitle ml="2">Review and Submit</AlertTitle>
                        </Alert>
                        <SimpleGrid templateColumns="auto 1fr" gap="4" alignItems="baseline" p="6">
                          <Box>
                            <Badge borderRadius="md" px="2" py="4px" bg="gray.100" color="blackAlpha.800">
                              <FontAwesomeIcon icon={[ 'fas', 'user' ]} size="1x" fixedWidth />
                            </Badge>
                          </Box>
                          <VStack align="flex-start">
                            <HStack>
                              <FontAwesomeIcon icon={[ 'far', 'envelope' ]} />
                              <Text>{`${formikBag.values.contributorEmail}`}</Text>
                            </HStack>
                            <Link
                              href={`https://www.instagram.com/${formikBag.values.contributorInstagram}/`}
                              isExternal
                              w="max-content"
                            >
                              <HStack>
                                <FontAwesomeIcon icon={[ 'fab', 'instagram' ]} />
                                <Text>{`${formikBag.values.contributorInstagram}`}</Text>
                              </HStack>
                            </Link>

                          </VStack>

                          <Box>
                            <Badge borderRadius="md" px="2" py="4px" bg="gray.100" color="blackAlpha.800">
                              <FontAwesomeIcon icon={[ 'fas', 'map-marker-alt' ]} size="1x" fixedWidth />
                            </Badge>
                          </Box>
                          <Box>
                            {formikBag.values.location.formatted_address}
                          </Box>

                          <Box>
                            <Badge borderRadius="md" px="2" py="4px" bg="gray.100" color="blackAlpha.800">
                              <FontAwesomeIcon icon={[ 'fas', 'quote-left' ]} size="1x" fixedWidth />
                            </Badge>
                          </Box>
                          <Box>
                            {formikBag.values.caption}
                          </Box>
                        </SimpleGrid>
                      </Box>
                    )}
                  </ModalBody>

                  <Divider userSelect="none" />

                  <ModalFooter p="2" bg="white" userSelect="none">
                    <ButtonGroup variant="unstyled" size="lg" spacing="24" w="full" justifyContent="center">
                      <IconButton
                        onClick={() => setCurrentStep(currentStep - 1)}
                        aria-label="Go back"
                        icon={<FontAwesomeIcon icon={[ 'fas', 'arrow-left' ]} size="lg" />}
                        disabled={isLoading || currentStep === 1}
                        color="brand.blue.600"
                      />
                      {currentStep < 3 ? (
                        <IconButton
                          onClick={onClickNext}
                          aria-label="Go forward"
                          disabled={!formikBag.isValid || (currentStep === 2 && formikBag.values.location.found === false)}
                          icon={<FontAwesomeIcon icon={[ 'fas', 'arrow-right' ]} size="lg" />}
                          color="brand.blue.600"
                        />
                      ) : (
                        <Button
                          type="submit"
                          variant="solid"
                          colorScheme="brand.red"
                          textTransform="uppercase"
                          disabled={isLoading}
                          isLoading={isLoading}
                          onClick={() => handleSubmit(formikBag)}
                        >
                          Submit
                        </Button>
                      )}
                    </ButtonGroup>
                  </ModalFooter>
                </ModalContent>
              </Modal>
              {formikBag.values.imageFile && (
                <FullscreenImage
                  isOpen={imgFullScreenOpen}
                  onClose={() => setImgFullScreenOpen(false)}
                  imgUrl={URL.createObjectURL(formikBag.values.imageFile)}
                />
              )}
            </form>
          )}
        </Formik>

        {!modalIsOpen && (
          <Button
            colorScheme="black"
            color="brand.blue.500"
            size="lg"
            h="4rem"
            w="12rem"
            fontSize="xl"
            onClick={handleModalOpen}
            mb="18"
          >
            Submit Hoops
          </Button>
        )}
      </div>
    </div>
  );
};

export default SubmitFormModal;
